// src/services/prefetchService.js

/**
 * Smart Prefetch Service for TimeMate Admin
 * Implements intelligent prefetching with safeguards against duplicate fetches
 *
 * Safeguards implemented (from PLAN.md Section 7.5.5):
 * 1. Cache check before fetch - prevents fetching data we already have
 * 2. Pending fetch tracking - prevents duplicate simultaneous requests
 * 3. Hover debounce - prevents rapid hover triggering many fetches
 * 4. Cancel on mouse leave - prevents fetching when user moves away quickly
 * 5. Error handling - services must be resilient
 */

import cacheService from './cacheService';

class PrefetchService {
  constructor() {
    /** @type {Set<string>} Track keys currently being fetched */
    this.pendingFetches = new Set();

    /** @type {Map<string, number>} Track hover timers for debouncing */
    this.hoverTimers = new Map();
  }

  /**
   * Smart prefetch - only fetches if NOT already cached or loading
   * @param {string} key - Cache key for the data
   * @param {Function} queryFn - Async function that fetches the data
   * @param {Object} options - Optional configuration
   * @param {number} options.ttl - TTL override for caching
   * @returns {Promise<any|null>} The fetched data or null if skipped/error
   */
  async prefetch(key, queryFn, options = {}) {
    const { ttl } = options;

    // SAFEGUARD 1: Check if already in cache
    const cached = await cacheService.get(key);
    if (cached) {
      // Already have data - skip fetch entirely
      return cached;
    }

    // SAFEGUARD 2: Check if already fetching (prevent duplicate requests)
    if (this.pendingFetches.has(key)) {
      // Already loading - skip duplicate fetch
      return null;
    }

    // SAFEGUARD 3: Mark as pending before fetching
    this.pendingFetches.add(key);
    try {
      const data = await queryFn();
      if (data !== null && data !== undefined) {
        await cacheService.set(key, data, ttl);
      }
      return data;
    } catch (error) {
      return null;
    } finally {
      this.pendingFetches.delete(key);
    }
  }

  /**
   * Hover prefetch with debounce - prevents rapid hover triggering fetches
   * @param {string} key - Cache key for the data
   * @param {Function} queryFn - Async function that fetches the data
   * @param {number} debounceMs - Milliseconds to wait before triggering fetch (default: 150ms)
   * @param {Object} options - Optional configuration for prefetch
   */
  prefetchOnHover(key, queryFn, debounceMs = 150, options = {}) {
    // SAFEGUARD 4: Clear any existing timer for this key
    if (this.hoverTimers.has(key)) {
      clearTimeout(this.hoverTimers.get(key));
    }

    // SAFEGUARD 5: Only trigger after sustained hover (not just passing over)
    const timer = setTimeout(() => {
      this.prefetch(key, queryFn, options);
      this.hoverTimers.delete(key);
    }, debounceMs);

    this.hoverTimers.set(key, timer);
  }

  /**
   * Cancel hover prefetch (user moved away before debounce completed)
   * @param {string} key - Cache key to cancel prefetch for
   */
  cancelHoverPrefetch(key) {
    if (this.hoverTimers.has(key)) {
      clearTimeout(this.hoverTimers.get(key));
      this.hoverTimers.delete(key);
    }
  }

  /**
   * Check if a key is currently being fetched
   * @param {string} key - Cache key to check
   * @returns {boolean} True if fetch is in progress
   */
  isPending(key) {
    return this.pendingFetches.has(key);
  }

  /**
   * Get all pending fetch keys
   * @returns {string[]} Array of keys currently being fetched
   */
  getPendingKeys() {
    return Array.from(this.pendingFetches);
  }

  /**
   * Get count of pending fetches
   * @returns {number} Number of pending fetches
   */
  getPendingCount() {
    return this.pendingFetches.size;
  }

  /**
   * Clear all pending fetches and timers
   * Call this on logout or cleanup
   */
  clear() {
    this.pendingFetches.clear();
    this.hoverTimers.forEach(timer => clearTimeout(timer));
    this.hoverTimers.clear();
  }
}

// Create singleton instance
export const prefetchService = new PrefetchService();
export default prefetchService;
